import { z } from './utils/zod.js';
import { dataResponse, deleteResponse, errorCodeResponse, successResponse } from './utils/response-templates.js';
import { webidAddressSchema, webidSexSchema, webidStartRequestSchema } from './kyc.js';
export const SAFE_TRANSFER_ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
];
export const safeTransferStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'EXPIRED', 'REVOKED', 'CONSUMED']);
export const safeTransferRecipientStatusSchema = z.enum([
    'PENDING',
    'LINK_OPENED',
    'KYC_IN_PROGRESS',
    'KYC_PASSED',
    'KYC_FAILED',
    'CODE_LOCKED',
    'DOWNLOADED',
    'EXPIRED',
    'REVOKED',
]);
export const safeTransferFileDtoSchema = z.object({
    id: z.string(),
    filename: z.string(),
    byteSize: z.string(),
    contentType: z.string(),
});
export const safeTransferRecipientDtoSchema = z.object({
    id: z.string(),
    email: z.string(),
    fullName: z.string().nullable(),
    status: safeTransferRecipientStatusSchema,
    downloadCount: z.number(),
    firstAccessedAt: z.string().datetime().nullable(),
    lastAccessedAt: z.string().datetime().nullable(),
    emailSentAt: z.string().datetime().nullable(),
    emailFailedAt: z.string().datetime().nullable(),
    verificationMethod: z.string().nullable(),
    termsAcceptedAt: z.string().datetime().nullable(),
    termsVersion: z.string().nullable(),
    codeAttempts: z.number(),
    codeGeneratedAt: z.string().datetime().nullable(),
    codeClaimedAt: z.string().datetime().nullable(),
    firstDownloadedAt: z.string().datetime().nullable(),
});
export const safeTransferDtoSchema = z.object({
    id: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    organizationId: z.string(),
    createdById: z.string(),
    subject: z.string(),
    message: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    oneTimeAccess: z.boolean(),
    requireKyc: z.boolean(),
    status: safeTransferStatusSchema,
    recipients: z.array(safeTransferRecipientDtoSchema),
    files: z.array(safeTransferFileDtoSchema),
});
export const safeTransferListItemDtoSchema = safeTransferDtoSchema.omit({ files: true });
export const createSafeTransferSchema = z.object({
    subject: z.string().trim().min(1).max(255),
    message: z.string().max(5000).optional(),
    expiresAt: z.string().datetime().optional(),
    oneTimeAccess: z.boolean().optional(),
    requireKyc: z.boolean().optional(),
});
export const updateSafeTransferSchema = createSafeTransferSchema.partial().extend({
    // null removes the expiry date
    expiresAt: z.string().datetime().nullable().optional(),
});
export const addSafeTransferRecipientSchema = z.object({
    email: z.string().commonEmail(),
    fullName: z.string().trim().min(1).max(150).optional(),
});
export const createSafeTransferResponseSchema = dataResponse(safeTransferDtoSchema);
export const updateSafeTransferResponseSchema = dataResponse(safeTransferDtoSchema);
export const getSafeTransferResponseSchema = dataResponse(safeTransferDtoSchema);
export const addSafeTransferRecipientResponseSchema = dataResponse(safeTransferRecipientDtoSchema);
export const removeSafeTransferRecipientResponseSchema = deleteResponse();
export const sendSafeTransferResponseSchema = dataResponse(safeTransferDtoSchema);
export const revokeSafeTransferResponseSchema = dataResponse(safeTransferDtoSchema);
export const safeTransferVatSchema = z.object({
    netCents: z.number().int().nonnegative(),
    vatCents: z.number().int().nonnegative(),
    totalCents: z.number().int().nonnegative(),
    ratePercent: z.number().nullable(),
    reverseCharge: z.boolean(),
});
export const safeTransferPricingSchema = z.object({
    pricePerRecipientCents: z.number().int().nonnegative(),
    currency: z.string(),
    // Stripe Tax result for the requested recipient count; null when it cannot
    // be computed (no recipients yet, no Stripe customer, unknown tax location).
    vat: safeTransferVatSchema.nullable(),
});
export const safeTransferPricingResponseSchema = dataResponse(safeTransferPricingSchema);
export const safeTransferAuditEventSchema = z.object({
    id: z.string(),
    action: z.string(),
    createdAt: z.string().datetime(),
    payload: z.unknown(),
});
export const getSafeTransferAuditResponseSchema = dataResponse(z.array(safeTransferAuditEventSchema));
export const listSafeTransfersPaginatedResponseSchema = z.object({
    success: z.boolean().openapi({ example: true }),
    data: z.array(safeTransferListItemDtoSchema),
    meta: z.object({
        total: z.number(),
        page: z.number(),
        limit: z.number(),
    }),
});
export const safeTransferNotFoundResponseSchema = errorCodeResponse('ESafeTransferNotFound', 'Safe transfer not found');
export const safeTransferNotEditableResponseSchema = errorCodeResponse('ESafeTransferNotEditable', 'Safe transfer cannot be modified after sending');
export const safeTransferRecipientNotFoundResponseSchema = errorCodeResponse('ESafeTransferRecipientNotFound', 'Recipient not found');
export const safeTransferRecipientDuplicatedResponseSchema = errorCodeResponse('ESafeTransferRecipientDuplicated', 'A recipient with this email already exists');
export const uploadSafeTransferFileRequestSchema = z.object({
    file: z.instanceof(File),
});
export const uploadSafeTransferFileResponseSchema = dataResponse(safeTransferFileDtoSchema);
export const deleteSafeTransferFileResponseSchema = deleteResponse();
export const safeTransferFileNotFoundResponseSchema = errorCodeResponse('ESafeTransferFileNotFound', 'File not found');
export const safeTransferInvalidFileTypeResponseSchema = errorCodeResponse('ESafeTransferInvalidFileType', 'File type not allowed or content does not match declared type');
export const safeTransferTotalSizeLimitResponseSchema = errorCodeResponse('ESafeTransferTotalSizeLimitReached', 'Total transfer size limit reached');
export const safeTransferNotUnlockedResponseSchema = errorCodeResponse('ESafeTransferNotUnlocked', 'A valid unlock session is required to download files');
export const safeTransferConsumedResponseSchema = errorCodeResponse('ESafeTransferConsumed', 'This one-time-access transfer has already been downloaded');
export const safeTransferNextActionSchema = z.enum([
    'KYC_REQUIRED',
    'CLAIM_CODE',
    'ENTER_CODE',
    'DOWNLOAD_AVAILABLE',
    'EXPIRED',
    'REVOKED',
    'CONSUMED',
]);
const recipientMeDataSchema = z.object({
    transfer: z.object({
        subject: z.string(),
        sender: z.object({
            fullName: z.string(),
            organization: z.string(),
        }),
        expiresAt: z.string().datetime().nullable(),
        oneTimeAccess: z.boolean(),
        fileCount: z.number(),
        totalByteSize: z.string(),
        files: z.array(safeTransferFileDtoSchema),
    }),
    recipient: z.object({
        email: z.string(),
        fullName: z.string().nullable(),
        kycStatus: safeTransferRecipientStatusSchema,
    }),
    nextAction: safeTransferNextActionSchema,
});
export const getRecipientMeResponseSchema = dataResponse(recipientMeDataSchema);
export const safeTransferIdAustriaNonceResponseSchema = dataResponse(z.object({ nonce: z.string() }));
export const safeTransferVerifyIdAustriaRequestSchema = z.object({
    code: z.string().min(1),
    termsAccepted: z.boolean().optional(),
    termsVersion: z.string().optional(),
    redirectUri: z.string().url().optional(),
});
export const safeTransferVerifyIdAustriaResponseSchema = successResponse();
export const safeTransferVerifyWebidStartRequestSchema = webidStartRequestSchema.extend({
    returnUrl: z.string().url(),
    preferredLanguage: z.string().optional(),
    termsAccepted: z.boolean().optional(),
});
export const safeTransferVerifyWebidStartResponseSchema = successResponse().extend({
    url: z.string(),
});
export const safeTransferVerificationFailedResponseSchema = errorCodeResponse('ESafeTransferVerificationFailed', 'Identity verification failed');
export const safeTransferUnlockSessionSchema = z.object({
    unlockToken: z.string(),
    unlockExpiresAt: z.string().datetime(),
});
export const safeTransferClaimCodeResponseSchema = dataResponse(safeTransferUnlockSessionSchema.extend({
    accessCode: z.string(),
}));
export const safeTransferUnlockRequestSchema = z.object({
    code: z.string().min(4).max(24),
});
export const safeTransferUnlockResponseSchema = dataResponse(safeTransferUnlockSessionSchema);
export const safeTransferCodeNotAvailableResponseSchema = errorCodeResponse('ESafeTransferCodeNotAvailable', 'Identity verification is required before an access code is available');
export const safeTransferCodeAlreadyClaimedResponseSchema = errorCodeResponse('ESafeTransferCodeAlreadyClaimed', 'The access code was already claimed');
export const safeTransferCodeInvalidResponseSchema = errorCodeResponse('ESafeTransferCodeInvalid', 'Invalid access code').extend({
    attemptsLeft: z.number().int().nonnegative(),
});
export const safeTransferCodeLockedResponseSchema = errorCodeResponse('ESafeTransferCodeLocked', 'Too many attempts — identity verification is required again');
export { webidAddressSchema, webidSexSchema };
